'use client'

import { useEffect, useState } from 'react'
import { Alert, Box, Button, Paper, Stack, Typography, alpha, useTheme } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import HomeIcon from '@mui/icons-material/Home'
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import { hatayiBildir } from '@/lib/hataTakibi/bildir'
import { reactHatasiniCoz } from '@/lib/hataTakibi/reactHataAciklamalari'
import { OPAS_DISPLAY_VERSION } from '@/generated/opasBuildMeta'
import { outfit } from '@/lib/fonts'

const yaziTipi = outfit.style.fontFamily

/**
 * Bir ekran çizilirken hata oluştuğunda kullanıcının gördüğü sayfa.
 *
 * Bu dosya olmadan Next.js boş beyaz bir ekran gösterir ve eczacı ne olduğunu anlamaz. Burada
 * hem anlaşılır bir açıklama verilir hem de hata sessizce merkeze bildirilir; kullanıcının
 * telefon açıp anlatmasını beklemeyiz.
 */
export default function Hata({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const theme = useTheme()
  const [kopyalandi, setKopyalandi] = useState(false)

  useEffect(() => {
    hatayiBildir(error)
  }, [error])

  useEffect(() => {
    if (!kopyalandi) return
    const sayac = window.setTimeout(() => setKopyalandi(false), 2500)
    return () => window.clearTimeout(sayac)
  }, [kopyalandi])

  /**
   * React üretim yapısında hata metnini numaraya indirger. Kullanıcıya «Minified React error
   * #185» yazmanın anlamı yok; karşılığı biliniyorsa günlük dille ne olduğu söylenir.
   */
  const sebep = reactHatasiniCoz(error.message)?.gunlukDil ?? null

  const destekMetni = [
    `OPAS ${OPAS_DISPLAY_VERSION}`,
    typeof window !== 'undefined' ? `Sayfa: ${window.location.pathname}` : null,
    `Zaman: ${new Date().toLocaleString('tr-TR')}`,
    error.digest ? `Destek kodu: ${error.digest}` : null,
    `Hata: ${error.message}`,
  ]
    .filter(Boolean)
    .join('\n')

  const panoyaKopyala = async () => {
    try {
      await navigator.clipboard.writeText(destekMetni)
      setKopyalandi(true)
    } catch {
      // Pano kapalıysa kullanıcıya ikinci bir hata gösterilmez; destek kodu zaten ekranda yazıyor.
    }
  }

  return (
    <Box
      sx={{
        minHeight: '70vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, sm: 3 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 560,
          width: '100%',
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: alpha(theme.palette.error.main, 0.28),
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ height: 4, bgcolor: 'error.main' }} />

        <Box sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              mx: 'auto',
              mb: 2.5,
              display: 'grid',
              placeItems: 'center',
              bgcolor: alpha(theme.palette.error.main, 0.12),
              color: 'error.main',
            }}
          >
            <ReportProblemOutlinedIcon sx={{ fontSize: 32 }} />
          </Box>

          <Typography sx={{ fontFamily: yaziTipi, fontSize: '1.2rem', fontWeight: 700, mb: 1 }}>
            Bu ekran açılamadı
          </Typography>

          <Typography
            sx={{ fontFamily: yaziTipi, fontSize: '0.9rem', lineHeight: 1.65, color: 'text.secondary' }}
          >
            Beklenmedik bir sorun oluştu ve durum ekibimize bildirildi. Kaydedilmemiş bir işiniz
            varsa kaybolmuş olabilir; tekrar denediğinizde çoğu zaman kaldığınız yerden devam
            edersiniz.
          </Typography>

          {sebep && (
            <Box
              sx={{
                mt: 2.5,
                px: 2,
                py: 1.25,
                borderRadius: 2,
                textAlign: 'left',
                bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.06 : 0.035),
              }}
            >
              <Typography
                sx={{ fontFamily: yaziTipi, fontSize: '0.72rem', fontWeight: 700, color: 'text.disabled', mb: 0.25 }}
              >
                NE OLDU
              </Typography>
              <Typography sx={{ fontFamily: yaziTipi, fontSize: '0.85rem', color: 'text.secondary' }}>
                {sebep}
              </Typography>
            </Box>
          )}

          {kopyalandi && (
            <Alert severity="success" sx={{ mt: 2.5, fontFamily: yaziTipi, textAlign: 'left' }}>
              Hata bilgisi panoya kopyalandı. Destek hattına yapıştırabilirsiniz.
            </Alert>
          )}

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.25}
            justifyContent="center"
            sx={{ mt: 3 }}
          >
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={reset}
              sx={{ fontFamily: yaziTipi, textTransform: 'none', fontWeight: 600 }}
            >
              Tekrar dene
            </Button>
            <Button
              variant="outlined"
              startIcon={<HomeIcon />}
              href="/t-dashboard"
              sx={{ fontFamily: yaziTipi, textTransform: 'none', fontWeight: 600 }}
            >
              Ana sayfaya dön
            </Button>
            <Button
              variant="text"
              startIcon={<ContentCopyIcon />}
              onClick={() => void panoyaKopyala()}
              sx={{ fontFamily: yaziTipi, textTransform: 'none', color: 'text.secondary' }}
            >
              Bilgiyi kopyala
            </Button>
          </Stack>

          <Typography
            sx={{ fontFamily: yaziTipi, fontSize: '0.72rem', color: 'text.disabled', display: 'block', mt: 3 }}
          >
            OPAS {OPAS_DISPLAY_VERSION}
            {error.digest ? ` · Destek kodu: ${error.digest}` : ''}
          </Typography>
        </Box>
      </Paper>
    </Box>
  )
}
